import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ClipboardList,
  HelpCircle,
  Loader2,
  Sparkles,
  Wand2
} from 'lucide-react';
import { getFieldLabel } from './docIntelUtils';
import { btnGhost, btnSecondary } from './DocIntelUi';
import DocIntelFieldPanel from './DocIntelFieldPanel';
import DocIntelTemplateLifecycle from './DocIntelTemplateLifecycle';

function LeakChecklist({
  sampleLeaks = [],
  isBusy,
  fixLeaksPending,
  onApplyFixes,
  onHighlightLeak
}) {
  const [decisions, setDecisions] = useState({});
  const [whyOpen, setWhyOpen] = useState(null);

  useEffect(() => {
    setDecisions((prev) => {
      const next = {};
      for (const leak of sampleLeaks) {
        const key = `${leak.canonical_name}:${leak.value}`;
        next[key] = prev[key] ?? 'accept';
      }
      return next;
    });
  }, [sampleLeaks]);

  if (!sampleLeaks.length) return null;

  const acceptCount = sampleLeaks.filter(
    (l) => decisions[`${l.canonical_name}:${l.value}`] !== 'skip'
  ).length;
  const skipCount = sampleLeaks.length - acceptCount;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 shadow-sm">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-950">
            Review sample leftovers ({sampleLeaks.length})
          </p>
          <p className="mt-0.5 text-xs font-medium text-amber-950">
            Accept to replace with a live field, or skip if this text should stay.
          </p>

          <ul className="mt-3 max-h-48 space-y-2 overflow-y-auto">
            {sampleLeaks.map((leak) => {
              const key = `${leak.canonical_name}:${leak.value}`;
              const decision = decisions[key] ?? 'accept';
              return (
                <li
                  key={key}
                  className="rounded-lg border border-amber-200/80 bg-white px-2.5 py-2 text-xs text-slate-800">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold">{getFieldLabel(leak.canonical_name)}</p>
                      <p className="mt-0.5 break-all text-amber-900/90">
                        <span className="line-through opacity-70">{leak.value}</span>
                        <span className="mx-1 text-slate-400">→</span>
                        <span className="font-medium text-emerald-800">
                          {getFieldLabel(leak.canonical_name)}
                        </span>
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-xs font-bold text-emerald-900">
                        <input
                          type="radio"
                          name={`leak-${key}`}
                          checked={decision === 'accept'}
                          onChange={() =>
                            setDecisions((d) => ({ ...d, [key]: 'accept' }))
                          }
                        />
                        Accept
                      </label>
                      <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-xs font-bold text-slate-700">
                        <input
                          type="radio"
                          name={`leak-${key}`}
                          checked={decision === 'skip'}
                          onChange={() => setDecisions((d) => ({ ...d, [key]: 'skip' }))}
                        />
                        Skip
                      </label>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-amber-900 hover:underline"
                    onClick={() => {
                      setWhyOpen((w) => (w === key ? null : key));
                      onHighlightLeak?.(leak);
                    }}>
                    <HelpCircle className="h-3 w-3" />
                    Why?
                  </button>
                  {whyOpen === key && (
                    <p className="mt-1 rounded bg-amber-50 px-2 py-1.5 text-xs font-medium leading-relaxed text-amber-950">
                      {leak.reason ||
                        'This looks like a specific sample value — wrong for other buyers.'}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            disabled={isBusy || fixLeaksPending || acceptCount === 0}
            onClick={() => {
              const only = [];
              const skip = [];
              for (const leak of sampleLeaks) {
                const key = `${leak.canonical_name}:${leak.value}`;
                if (decisions[key] === 'skip') skip.push(leak.canonical_name);
                else only.push(leak.canonical_name);
              }
              onApplyFixes?.({ only, skip, acceptCount, skipCount });
            }}
            className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-white px-2.5 py-2 text-xs font-semibold text-amber-950 hover:bg-amber-100 disabled:opacity-50">
            {fixLeaksPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Wand2 className="h-3.5 w-3.5" />
            )}
            Apply {acceptCount} fix{acceptCount === 1 ? '' : 'es'}
            {skipCount > 0 ? ` · skip ${skipCount}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

const DocIntelStudioSidebar = ({
  name,
  onNameChange,
  branding,
  onBrandingChange,
  includeLogo,
  includeWatermark,
  onIncludeLogoChange,
  onIncludeWatermarkChange,
  placeholders = [],
  fieldMappings = [],
  sampleLeaks = [],
  entityAudit = null,
  templateTables = [],
  onMappingsChange,
  onInsertField,
  onOpenReview,
  onFixLeaks,
  onHighlightLeak,
  fixLeaksPending = false,
  onRebuild,
  rebuildPending = false,
  templateId,
  projectId,
  isPromoted = false,
  isBusy = false,
  projectStaticChecklist = null,
  projectStaticConfirmedAt = null,
  onProjectStaticConfirmed,
  onRestored,
  activeField = null,
  onHoverField,
  onLocateInDocument
}) => {
  const accent = branding.accentColor || '#0A6C5A';
  void templateTables;

  return (
    <aside className="flex max-h-[calc(100vh-140px)] flex-col gap-3 overflow-y-auto lg:sticky lg:top-2">
      {onOpenReview ? (
        <button
          type="button"
          disabled={isBusy}
          onClick={onOpenReview}
          className={`${btnGhost} w-full justify-start border border-slate-200 bg-white shadow-sm`}>
          <ClipboardList className="h-4 w-4 text-teal-700" />
          Review extracted fields
        </button>
      ) : null}

      <details className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 sm:px-4 [&::-webkit-details-marker]:hidden">
          <span className="flex items-center gap-2">
            Template branding
            <span
              className="h-3.5 w-3.5 rounded-full ring-1 ring-slate-200"
              style={{ backgroundColor: accent }}
            />
          </span>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </summary>
        <div className="space-y-3 border-t border-slate-100 px-3 py-3 sm:px-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">
              Template name
            </label>
            <input
              type="text"
              value={name}
              disabled={isBusy || isPromoted}
              onChange={(e) => onNameChange?.(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">
              Accent colour
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={accent}
                disabled={isBusy}
                onChange={(e) => onBrandingChange?.({ accentColor: e.target.value })}
                className="h-9 w-12 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
              />
              <input
                type="text"
                value={accent}
                disabled={isBusy}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) {
                    onBrandingChange?.({ accentColor: v });
                  }
                }}
                className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 font-mono text-xs"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Builder
              </p>
              <p className="truncate font-medium text-slate-800">
                {branding.companyName || '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Project
              </p>
              <p className="truncate font-medium text-slate-800">
                {branding.projectName || '—'}
              </p>
            </div>
          </div>
          <div className="space-y-2 border-t border-slate-100 pt-2 text-sm text-slate-700">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={includeLogo}
                disabled={isBusy || isPromoted}
                onChange={(e) => onIncludeLogoChange?.(e.target.checked)}
              />
              <span className="text-xs font-medium">Logo in PDF header</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={includeWatermark}
                disabled={isBusy || isPromoted}
                onChange={(e) => onIncludeWatermarkChange?.(e.target.checked)}
              />
              <span className="text-xs font-medium">Watermark on body</span>
            </label>
          </div>
        </div>
      </details>

      {!isPromoted && entityAudit?.summary && (
        <div
          className={`rounded-xl border p-3 text-[12px] shadow-sm ${
            entityAudit.findings_count > 0
              ? 'border-amber-200 bg-amber-50/80 text-amber-950'
              : 'border-emerald-200 bg-emerald-50/70 text-emerald-950'
          }`}>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-700">
            Entity consistency
          </p>
          <p className="mt-1 leading-snug">{entityAudit.summary}</p>
        </div>
      )}

      {!isPromoted && sampleLeaks.length > 0 && (
        <LeakChecklist
          sampleLeaks={sampleLeaks}
          isBusy={isBusy}
          fixLeaksPending={fixLeaksPending}
          onApplyFixes={onFixLeaks}
          onHighlightLeak={onHighlightLeak}
        />
      )}

      <DocIntelFieldPanel
        placeholders={placeholders}
        fieldMappings={fieldMappings}
        sampleLeaks={sampleLeaks}
        onChange={onMappingsChange}
        onInsertField={onInsertField}
        disabled={isBusy || isPromoted}
        activeField={activeField}
        onHoverField={onHoverField}
        onLocateInDocument={onLocateInDocument}
      />

      <details className="rounded-xl border border-slate-200 bg-white shadow-sm" open>
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50">
          Version history &amp; project checks
        </summary>
        <div className="space-y-3 border-t border-slate-100 p-3 sm:p-4">
          {templateId && (
            <DocIntelTemplateLifecycle
              templateId={templateId}
              currentProjectId={projectId}
              isPromoted={isPromoted}
              projectStaticChecklist={projectStaticChecklist}
              projectStaticConfirmedAt={projectStaticConfirmedAt}
              onProjectStaticConfirmed={onProjectStaticConfirmed}
              onRestored={onRestored}
            />
          )}
        </div>
      </details>

      <details className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50">
          Advanced
        </summary>
        <div className="space-y-3 border-t border-slate-100 p-4">
          {!isPromoted && templateId && (
            <button
              type="button"
              disabled={isBusy || rebuildPending}
              onClick={onRebuild}
              className={`${btnSecondary} w-full`}>
              {rebuildPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 text-teal-700" />
              )}
              Rebuild from source
            </button>
          )}
        </div>
      </details>
    </aside>
  );
};

export default DocIntelStudioSidebar;
